import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Tailwind CSS class merger utility
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Utility functions for formatting, calculations, and common operations.
 */

/**
 * Format cents to dollars string
 */
export function formatCentsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Check if a date has expired
 * Uses UTC time to ensure consistency across all devices/timezones
 */
export function isExpired(expiresAt: string): boolean {
  const expiresAtUTC = new Date(expiresAt).getTime();
  const nowUTC = Date.now();
  return expiresAtUTC < nowUTC;
}

/**
 * Get the actual bounty status considering expiration date
 * This ensures status is accurate even if database hasn't been updated yet
 * PHOENIX EXPIRATION RULE: Expired status overrides ALL other states
 */
export function getActualBountyStatus(status: string, expiresAt: string): string {
  // PHOENIX: If expires_at has passed, status is ALWAYS 'expired' regardless of database status
  if (isExpired(expiresAt)) {
    return 'expired';
  }
  return status;
}

/**
 * Check if a bounty can be claimed
 * Returns true only if bounty is open and not expired
 */
export function canClaimBounty(status: string, expiresAt: string): boolean {
  const actualStatus = getActualBountyStatus(status, expiresAt);
  return actualStatus === 'open';
}

/**
 * Format file name for storage (sanitize and add timestamp)
 */
export function formatStorageFileName(userId: string, bountyId: string, fileName: string): string {
  const timestamp = Date.now();
  return `${userId}/${bountyId}-${timestamp}-${fileName}`;
}

/**
 * Extract object path from storage URL
 */
export function extractStoragePathFromUrl(url: string, bucketName: string): string | null {
  const regex = new RegExp(`\\/${bucketName}\\/(.+?)(\\?|$)`);
  const match = url.match(regex);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

/**
 * Debounce function to limit execution rate
 */
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Safely parse JSON with error handling
 */
export function safeJsonParse<T>(json: string | null | undefined, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/**
 * Create a delay promise for async operations
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Strip HTML tags from a string
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '');
}

/**
 * Photo set proof requirement (e.g. "Photo set: 3 labeled photos (Place, Meal 1, Meal 2)")
 */
export interface PhotoSetRequirement {
  min: number;
  labels: string[];
}

/**
 * Parse a "Photo set: N labeled photos (Label 1, Label 2, ...)" requirement from a
 * bounty's proof_requirements array. Returns null when no photo-set requirement exists.
 */
export function parsePhotoSetRequirement(requirements: unknown[] | null | undefined): PhotoSetRequirement | null {
  if (!Array.isArray(requirements)) return null;
  for (const req of requirements) {
    if (typeof req !== 'string') continue;
    const match = req.toLowerCase().match(/photo set[^0-9]*([0-9]+)(?:[^()]*\(([^)]*)\))?/);
    if (!match) continue;
    const min = parseInt(match[1], 10);
    const count = Number.isFinite(min) && min > 0 ? min : 1;
    const labels = match[2]
      ? match[2].split(',').map((l) => l.trim()).filter(Boolean)
      : Array.from({ length: count }, (_, i) => `Photo ${i + 1}`);
    return { min: count, labels };
  }
  return null;
}

